import { Component, EventEmitter, Input, Output } from '@angular/core';

import { BibliographicResource } from '®shared/bibliographic-resource';
import {
  DestinationSubtype,
  DestinationType,
  ExternalLinkClickEvent,
  LinkPositionOnPage,
  getExternalLinkClickEventProperties,
} from '®shared/list-item/events';
import { EditableItem } from '®shared/reading-list/models/editable-item';
import { UriService } from '®shared/url-utils';

import { LoadableLibraryAvailability } from './library-availability';

/**
 * A component to display the current resource's library holdings
 **/
@Component({
  selector: 'rl-library-availability',
  templateUrl: './library-availability.component.html',
})
export class LibraryAvailabilityComponent {
  @Input() physicalResources: LoadableLibraryAvailability;
  @Input() resource: BibliographicResource;
  @Input() digitisationLink?: string;
  @Input() item: EditableItem;
  @Output() onlineResourceClick = new EventEmitter<ExternalLinkClickEvent>();

  constructor(private uriService: UriService) {}

  relativeToBaseUri(externalLink: string): string {
    return this.uriService.getExternalLinkRelativeToBaseUri(externalLink);
  }

  /**
   * Indicates the resource is unavailable online given no digitisation or
   * any valid online resources.
   * @returns {boolean}
   */
  isUnavailableOnline(): boolean {
    return (
      !this.digitisationLink &&
      !(
        this.resource &&
        this.resource.allOnlineResources &&
        this.resource.allOnlineResources.length
      )
    );
  }

  /**
   * Indicates the resource is unavailable physically given no valid physical
   * resources.
   */
  isUnavailablePhysically(): boolean {
    return (
      !this.physicalResources ||
      (this.physicalResources.isLoading !== true &&
        !this.hasPhysicalLocations())
    );
  }

  /**
   * Returns true if there is at least one physical resource with a location.
   * @returns {boolean}
   */
  hasPhysicalLocations(): boolean {
    return (
      this.physicalResources.data &&
      this.physicalResources.data.locations &&
      this.physicalResources.data.locations.length > 0
    );
  }

  /**
   * Indicates whether the resource has a library_view_url to display.
   * @returns {boolean}
   */
  hasLibraryViewUrl(): boolean {
    return this.physicalResources &&
      this.physicalResources.isLoading !== true &&
      this.physicalResources.data &&
      this.physicalResources.data.library_view_url
      ? true
      : false;
  }

  /**
   * Indicates whether the resource has any alternative view_in_catalogue_urls to display.
   * @returns {boolean}
   */
  hasAlternativeCatalogueViewUrls(): boolean {
    return this.physicalResources &&
      this.physicalResources.isLoading !== true &&
      this.physicalResources.data &&
      this.physicalResources.data.catalogues &&
      this.physicalResources.data.catalogues.length > 0
      ? true
      : false;
  }

  /**
   * Indicates whether or not other editions or formats makes sense for this resource.
   * @returns {boolean}
   */
  isRelevantForLibrarySearch(): boolean {
    return this.resource &&
      this.resource.isBookOrChapterResource() &&
      this.physicalResources &&
      this.physicalResources.isLoading !== true &&
      this.physicalResources.data &&
      this.physicalResources.data.library_search_url
      ? true
      : false;
  }

  trackDigitisationClick(digitisationLink: string) {
    this.onlineResourceClick.emit(
      getExternalLinkClickEventProperties(
        digitisationLink,
        LinkPositionOnPage.ITEM_AVAILABILITY_SECTION,
        DestinationType.CONTENT,
        DestinationSubtype.CONTENT_DIGITISATION,
      ),
    );
  }

  trackLibrarySearchClick(url: string): void {
    this.onlineResourceClick.emit(
      getExternalLinkClickEventProperties(
        url,
        LinkPositionOnPage.ITEM_AVAILABILITY_SECTION,
        DestinationType.LIBRARY_CATALOGUE,
        DestinationSubtype.LIBRARY_CATALOGUE_SEARCH,
      ),
    );
  }

  trackLibraryViewClick(url: string): void {
    this.onlineResourceClick.emit(
      getExternalLinkClickEventProperties(
        url,
        LinkPositionOnPage.ITEM_AVAILABILITY_SECTION,
        DestinationType.LIBRARY_CATALOGUE,
        DestinationSubtype.LIBRARY_CATALOGUE_LCN_LOOKUP,
      ),
    );
  }
}
